import React from 'react'

import { Field, Form, Formik } from 'formik'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import TextMultiInput from '@/components/common/TextMultiInput'
import { Combobox } from '@/components/filter/Combobox'
import FilterContainer from '@/components/filter/FilterContainer'
import AutoSubmitToken from '@/components/form/AutoSubmitToken'
import ApplyFiltersButton from '@/components/search/ApplyFiltersButton'
import DateFilter from '@/components/search/DateFilter'
import FilterChip from '@/components/search/FilterChip'
import FilterHeader from '@/components/search/FilterHeader'

import { DEFAULT_SBIR_FILTER, PHASE_MAP } from '@/constants/search/federal/sbir'

import { calcNumDiffSbirSearchFilter } from '@/helpers/search/filter'

import { useMounted } from '@/hooks/common/useMounted'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useSbirAgencySearch } from '@/hooks/search/useSbirAgencySearch'
import { useSbirBranchSearch } from '@/hooks/search/useSbirBranchSearch'
import { useSbirPhaseSearch } from '@/hooks/search/useSbirPhaseSearch'
import { useSbirProgramSearch } from '@/hooks/search/useSbirProgramSearch'
import { useSbirTopicBranchSearch } from '@/hooks/search/useSbirTopicBranchSearch'

import { useSbirSearchStore } from '@/stores/SbirSearchStore'

import type { HeaderConfig } from '@/types/components/SearchFilter'
import type {
  SbirAgency,
  SbirBranch,
  SbirSearchFilter,
  TopicBranch,
} from '@/types/sbir/Filter'

interface SBIRFilter {
  filter?: SbirSearchFilter | null
  onSaveChanges: (filter: SbirSearchFilter) => void
  autoSubmit?: boolean
  headerConfig?: HeaderConfig
  isSchedule?: boolean
  showApply?: boolean
}

const SBIRFilter: React.FC<SBIRFilter> = ({
  filter = DEFAULT_SBIR_FILTER,
  onSaveChanges,
  autoSubmit = false,
  headerConfig = {
    show: true,
  },
  isSchedule = false,
  showApply = true,
}) => {
  const { accessToken } = useAuthInfo()
  const { activeOrgId } = useActiveOrgId()
  const {
    query: agencyQuery,
    setQuery: setAgencyQuery,
    data: agencyResults,
  } = useSbirAgencySearch({ initialAgencyIds: DEFAULT_SBIR_FILTER.agencies })

  const {
    query: branchQuery,
    setQuery: setBranchQuery,
    data: branchResults,
  } = useSbirBranchSearch({ initialBranchIds: DEFAULT_SBIR_FILTER.branches })

  const {
    query: phaseQuery,
    setQuery: setPhaseQuery,
    data: phaseResults,
  } = useSbirPhaseSearch()

  const {
    query: programQuery,
    setQuery: setProgramQuery,
    data: programResults,
  } = useSbirProgramSearch()

  const {
    query: topicBranchQuery,
    setQuery: setTopicBranchQuery,
    data: topicBranchResults,
  } = useSbirTopicBranchSearch({
    initialTopicBranchIds: DEFAULT_SBIR_FILTER.topic_branches,
  })

  const isMounted = useMounted()

  const { query, request, setRequest, setFilterDrawerIsOpen } =
    useSbirSearchStore()

  const applyFilters = () => {
    setRequest(
      {
        query: query,
        type: request?.type ?? 'sbir',
        organization_id: activeOrgId,
        filter: {
          type: 'sbir',
          filter: filter ?? DEFAULT_SBIR_FILTER,
        },
        use_fts: false,
      },
      accessToken,
    )
    setFilterDrawerIsOpen(false)
  }

  if (!isMounted) {
    return
  }

  return (
    <Formik
      initialValues={filter ?? DEFAULT_SBIR_FILTER}
      onSubmit={onSaveChanges}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, setValues }) => {
        return (
          <Form
            id="federal-grant-filter-form"
            className="flex flex-col gap-y-[14px] divide-y divide-primary-200/80 dark:divide-primary-800"
          >
            {autoSubmit && <AutoSubmitToken />}
            <div className="flex flex-col">
              <div
                className={twMerge(
                  'flex flex-col sticky top-0 bg-white dark:bg-zinc-900 z-10 py-1',
                  headerConfig.containerClassName,
                )}
              >
                {' '}
                {headerConfig.show && (
                  <FilterHeader
                    onReset={() => setValues(DEFAULT_SBIR_FILTER)}
                    count={calcNumDiffSbirSearchFilter(values)}
                  />
                )}
                {showApply && (
                  <div className="flex flex-wrap px-4 gap-2 pb-4">
                    <ApplyFiltersButton onClick={applyFilters} />
                  </div>
                )}
              </div>
              <div className="flex flex-col h-full w-full">
                {!isSchedule && (
                  <DateFilter value={values} setFieldValue={setFieldValue} />
                )}
                <FilterContainer
                  title="Agencies"
                  count={values.agencies.length}
                  onClear={() => setFieldValue('agencies', [])}
                >
                  <Field type="text" name="agencies">
                    {({
                      field,
                    }: {
                      field: {
                        name: string
                        value: Array<string>
                        onChange: (values: Array<string>) => void
                      }
                    }) => (
                      <div className="flex flex-col gap-y-3">
                        <Combobox<SbirAgency>
                          query={agencyQuery}
                          options={agencyResults}
                          selectedIds={field.value}
                          placeholder="Ex: Department of Defense"
                          itemIdFn={(agency) => agency.name}
                          itemDisplayFn={(agency) => agency.name}
                          setQuery={setAgencyQuery}
                          onAdd={(agency: SbirAgency) =>
                            setFieldValue('agencies', [
                              ...field.value,
                              agency.name,
                            ])
                          }
                          onRemove={(agency: SbirAgency) =>
                            setFieldValue(
                              'agencies',
                              field.value.filter((a) => a !== agency.name),
                            )
                          }
                        />
                        {values.agencies !== undefined &&
                          values.agencies.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {values.agencies.map((agency) => {
                                return (
                                  <FilterChip
                                    key={agency}
                                    content={agency}
                                    onDelete={() =>
                                      setFieldValue(
                                        'agencies',
                                        values.agencies.filter(
                                          (a) => a !== agency,
                                        ),
                                      )
                                    }
                                  />
                                )
                              })}
                            </div>
                          )}
                      </div>
                    )}
                  </Field>
                </FilterContainer>
                <FilterContainer
                  title="Branches"
                  count={values.branches.length}
                  onClear={() => setFieldValue('branches', [])}
                >
                  <Field type="text" name="branches">
                    {({
                      field,
                    }: {
                      field: {
                        name: string
                        value: Array<string>
                        onChange: (values: Array<string>) => void
                      }
                    }) => (
                      <div className="flex flex-col gap-y-3">
                        <Combobox
                          query={branchQuery}
                          options={branchResults}
                          selectedIds={field.value}
                          placeholder="Ex: Air Force"
                          itemIdFn={(branch) => branch.name}
                          itemDisplayFn={(branch) => branch.name}
                          setQuery={setBranchQuery}
                          onAdd={(branch: SbirBranch) =>
                            setFieldValue('branches', [
                              ...field.value,
                              branch.name,
                            ])
                          }
                          onRemove={(branch: SbirBranch) =>
                            setFieldValue(
                              'branches',
                              field.value.filter((b) => b !== branch.name),
                            )
                          }
                        />
                        {values.branches !== undefined &&
                          values.branches.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {values.branches.map((branch) => {
                                return (
                                  <FilterChip
                                    key={branch}
                                    content={branch}
                                    onDelete={() =>
                                      setFieldValue(
                                        'branches',
                                        values.branches.filter(
                                          (b) => b !== branch,
                                        ),
                                      )
                                    }
                                  />
                                )
                              })}
                            </div>
                          )}
                      </div>
                    )}
                  </Field>
                </FilterContainer>
                <FilterContainer
                  title="Number(s)"
                  count={values.numbers.length}
                  onClear={() => setFieldValue('numbers', [])}
                >
                  <Field type="text" name="numbers">
                    {({
                      field,
                    }: {
                      field: {
                        name: string
                        value: Array<string>
                        onChange: (values: Array<string>) => void
                      }
                    }) => (
                      <div className="flex flex-col gap-y-3">
                        <TextMultiInput
                          className="h-[30px] px-2 rounded-[4px]"
                          placeholder="Ex: PA-23-230"
                          onSubmit={(value: string) =>
                            setFieldValue('numbers', [...field.value, value])
                          }
                        />
                        {values.numbers !== undefined &&
                          values.numbers.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {values.numbers.map((number) => {
                                return (
                                  <FilterChip
                                    key={number}
                                    content={number}
                                    variant={'primary'}
                                    onDelete={() =>
                                      setFieldValue(
                                        'numbers',
                                        values.numbers.filter(
                                          (n) => n !== number,
                                        ),
                                      )
                                    }
                                  />
                                )
                              })}
                            </div>
                          )}
                      </div>
                    )}
                  </Field>
                </FilterContainer>
                <FilterContainer
                  title="Phase(s)"
                  count={values.phases.length}
                  onClear={() => setFieldValue('phases', [])}
                >
                  <Field type="text" name="phases">
                    {({
                      field,
                    }: {
                      field: {
                        name: string
                        value: Array<string>
                        onChange: (values: Array<string>) => void
                      }
                    }) => (
                      <div className="flex flex-col gap-y-3">
                        <Combobox<string>
                          query={phaseQuery}
                          options={phaseResults}
                          selectedIds={field.value}
                          placeholder="Ex: Phase II"
                          itemIdFn={(phase) => phase}
                          itemDisplayFn={(phase) => PHASE_MAP[phase]}
                          showInput={false}
                          setQuery={setPhaseQuery}
                          onAdd={(phase: string) =>
                            setFieldValue('phases', [...field.value, phase])
                          }
                          onRemove={(phase: string) =>
                            setFieldValue(
                              'phases',
                              field.value.filter((a) => a !== phase),
                            )
                          }
                        />
                        {values.phases !== undefined &&
                          values.phases.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {values.phases.map((phase) => {
                                return (
                                  <FilterChip
                                    key={phase}
                                    content={PHASE_MAP[phase]}
                                    onDelete={() =>
                                      setFieldValue(
                                        'phases',
                                        values.phases.filter(
                                          (p) => p !== phase,
                                        ),
                                      )
                                    }
                                  />
                                )
                              })}
                            </div>
                          )}
                      </div>
                    )}
                  </Field>
                </FilterContainer>
                <FilterContainer
                  title="Program(s)"
                  count={values.programs.length}
                  onClear={() => setFieldValue('programs', [])}
                >
                  <Field type="text" name="programs">
                    {({
                      field,
                    }: {
                      field: {
                        name: string
                        value: Array<string>
                        onChange: (values: Array<string>) => void
                      }
                    }) => (
                      <div className="flex flex-col gap-y-3">
                        <Combobox
                          query={programQuery}
                          options={programResults}
                          selectedIds={field.value}
                          placeholder="Ex: SBIR"
                          showInput={false}
                          itemIdFn={(program) => program}
                          itemDisplayFn={(program) => program}
                          setQuery={setProgramQuery}
                          onAdd={(program: string) =>
                            setFieldValue('programs', [...field.value, program])
                          }
                          onRemove={(program: string) =>
                            setFieldValue(
                              'programs',
                              field.value.filter((a) => a !== program),
                            )
                          }
                        />
                        {values.programs !== undefined &&
                          values.programs.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {values.programs.map((program) => {
                                return (
                                  <FilterChip
                                    key={program}
                                    content={program}
                                    onDelete={() =>
                                      setFieldValue(
                                        'programs',
                                        values.programs.filter(
                                          (p) => p !== program,
                                        ),
                                      )
                                    }
                                  />
                                )
                              })}
                            </div>
                          )}
                      </div>
                    )}
                  </Field>
                </FilterContainer>
                <FilterContainer
                  title="Topic Branches"
                  count={values.topic_branches.length}
                  onClear={() => setFieldValue('topic_branches', [])}
                >
                  <Field type="text" name="topic_branches">
                    {({
                      field,
                    }: {
                      field: {
                        name: string
                        value: Array<string>
                        onChange: (values: Array<string>) => void
                      }
                    }) => (
                      <div className="flex flex-col gap-y-3">
                        <Combobox<TopicBranch>
                          query={topicBranchQuery}
                          options={topicBranchResults}
                          selectedIds={field.value}
                          placeholder="Ex: Navy"
                          itemIdFn={(topicBranch) => topicBranch.name}
                          itemDisplayFn={(topicBranch) => topicBranch.name}
                          setQuery={setTopicBranchQuery}
                          onAdd={(branch: TopicBranch) =>
                            setFieldValue('topic_branches', [
                              ...field.value,
                              branch.name,
                            ])
                          }
                          onRemove={(branch: TopicBranch) =>
                            setFieldValue(
                              'topic_branches',
                              field.value.filter((a) => a !== branch.name),
                            )
                          }
                        />
                        {values.topic_branches !== undefined &&
                          values.topic_branches.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {values.topic_branches.map((topicBranch) => {
                                return (
                                  <FilterChip
                                    key={topicBranch}
                                    content={topicBranch}
                                    onDelete={() =>
                                      setFieldValue(
                                        'topic_branches',
                                        values.topic_branches.filter(
                                          (p) => p !== topicBranch,
                                        ),
                                      )
                                    }
                                  />
                                )
                              })}
                            </div>
                          )}
                      </div>
                    )}
                  </Field>
                </FilterContainer>
                <FilterContainer
                  title="Topic Number(s)"
                  count={values.topic_numbers.length}
                  onClear={() => setFieldValue('topic_numbers', [])}
                >
                  <Field type="text" name="topic_numbers">
                    {({
                      field,
                    }: {
                      field: {
                        name: string
                        value: Array<string>
                        onChange: (values: Array<string>) => void
                      }
                    }) => (
                      <div className="flex flex-col gap-y-3">
                        <TextMultiInput
                          className="h-[30px] px-2 rounded-[4px]"
                          placeholder="Ex: A244-002"
                          onSubmit={(value: string) =>
                            setFieldValue('topic_numbers', [
                              ...field.value,
                              value,
                            ])
                          }
                        />
                        {values.topic_numbers !== undefined &&
                          values.topic_numbers.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {values.topic_numbers.map((topicNumber) => {
                                return (
                                  <FilterChip
                                    key={topicNumber}
                                    content={topicNumber}
                                    variant={'primary'}
                                    onDelete={() =>
                                      setFieldValue(
                                        'topic_numbers',
                                        values.topic_numbers.filter(
                                          (t) => t !== topicNumber,
                                        ),
                                      )
                                    }
                                    className="!text-xs py-0.5"
                                  />
                                )
                              })}
                            </div>
                          )}
                      </div>
                    )}
                  </Field>
                </FilterContainer>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default SBIRFilter
